import React, { useEffect } from 'react';
import { useLazyGetDealSummaryQuery } from '../../services/AiSummary';

export default function AiSidePanel({ isOpen, onClose, baseline, signals, listingId }) {
  const [fetchSummary, { data, isLoading, isError, isFetching }] = useLazyGetDealSummaryQuery();

  // Zusammenfassung laden sobald Panel geöffnet wird (und listingId vorhanden ist)
  useEffect(() => {
    if (isOpen && listingId) {
      fetchSummary(listingId);
    }
  }, [isOpen, listingId, fetchSummary]);

  const risks   = signals?.filter(s => s.type === 'risk' || s.type === 'caution') || [];
  const chances = signals?.filter(s => s.type === 'positive') || [];
  const summary = data?.summary || null;
  const loading = isLoading || isFetching;

  return (
    <>
      {/* Backdrop */}
      {isOpen && (
        <div
          className="fixed inset-0 z-[199] transition-opacity duration-300"
          style={{ background: 'rgba(12,59,59,0.15)' }}
          onClick={onClose}
        />
      )}

      {/* Panel */}
      <div
        className="fixed top-0 right-0 bottom-0 w-[400px] bg-white z-[200] flex flex-col transition-transform duration-300 ease-out"
        style={{
          transform: isOpen ? 'translateX(0)' : 'translateX(100%)',
          boxShadow: isOpen ? '-4px 0 24px rgba(0,0,0,0.08)' : 'none',
        }}
      >
        {/* Header */}
        <div
          className="flex items-start justify-between shrink-0"
          style={{ background: '#1DB3B3', padding: '16px 20px' }}
        >
          <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <span style={{ fontSize: 16, fontWeight: 600, lineHeight: '24px', color: '#FFFFFF' }}>
              Deal-Zusammenfassung
            </span>
            <span style={{ fontSize: 12, fontWeight: 400, lineHeight: '18px', color: 'rgba(255,255,255,0.65)' }}>
              Automatische Auswertung basierend auf Score, Signalen und Marktdaten
            </span>
          </div>

          <button
            onClick={onClose}
            style={{
              width: 32,
              height: 32,
              borderRadius: 8,
              border: 'none',
              background: 'transparent',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 18,
              color: 'rgba(255,255,255,0.80)',
              cursor: 'pointer',
              transition: 'background 150ms, color 150ms',
              alignSelf: 'flex-start',
              flexShrink: 0,
              marginLeft: 12,
            }}
            onMouseEnter={e => {
              e.currentTarget.style.background = 'rgba(255,255,255,0.12)';
              e.currentTarget.style.color = '#FFFFFF';
            }}
            onMouseLeave={e => {
              e.currentTarget.style.background = 'transparent';
              e.currentTarget.style.color = 'rgba(255,255,255,0.80)';
            }}
          >
            ×
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto" style={{ padding: 20 }}>

          {/* Loading State */}
          {loading && (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 12, paddingTop: 40 }}>
              <div style={{
                width: 28,
                height: 28,
                border: '3px solid #E8F7F7',
                borderTopColor: '#1DB3B3',
                borderRadius: '50%',
                animation: 'spin 0.7s linear infinite',
              }} />
              <span style={{ fontSize: 14, color: '#8C8FA0', lineHeight: '20px' }}>
                Analyse wird erstellt…
              </span>
              <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
            </div>
          )}

          {/* Fehlerfall */}
          {isError && !loading && (
            <div style={{
              background: '#FDEAEA',
              border: '1px solid rgba(232,56,56,0.20)',
              borderRadius: 8,
              padding: 16,
              marginBottom: 12,
            }}>
              <span style={{ fontSize: 14, color: '#E83838', lineHeight: '20px' }}>
                Zusammenfassung konnte nicht geladen werden. Bitte versuche es erneut.
              </span>
            </div>
          )}

          {/* Inhalt */}
          {!loading && summary && (
            <>
              {/* AI-Text */}
              <p style={{
                fontSize: 15,
                fontWeight: 400,
                lineHeight: 1.7,
                color: '#5D5D6D',
                marginBottom: 20,
                whiteSpace: 'pre-wrap',
              }}>
                {summary}
              </p>

              {/* Risiko-Card */}
              {risks.length > 0 && (
                <div style={{
                  background: '#FDEAEA',
                  border: '1px solid rgba(232,56,56,0.20)',
                  borderRadius: 8,
                  padding: 12,
                  marginBottom: 12,
                }}>
                  <div style={{
                    fontSize: 12,
                    fontWeight: 600,
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    color: '#E83838',
                    marginBottom: 6,
                  }}>
                    RISIKEN
                  </div>
                  <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: 4 }}>
                    {risks.slice(0, 3).map((r, i) => (
                      <li key={i} style={{ fontSize: 14, lineHeight: '20px', color: '#5D5D6D' }}>
                        <span style={{ color: '#E83838' }}>•</span> {r.label || r.key}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Chancen-Card */}
              {chances.length > 0 && (
                <div style={{
                  background: '#E8F7E6',
                  border: '1px solid rgba(31,165,52,0.20)',
                  borderRadius: 8,
                  padding: 12,
                  marginBottom: 12,
                }}>
                  <div style={{
                    fontSize: 12,
                    fontWeight: 600,
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    color: '#1FA534',
                    marginBottom: 6,
                  }}>
                    CHANCEN
                  </div>
                  <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: 4 }}>
                    {chances.slice(0, 3).map((c, i) => (
                      <li key={i} style={{ fontSize: 14, lineHeight: '20px', color: '#5D5D6D' }}>
                        <span style={{ color: '#1FA534' }}>•</span> {c.label || c.key}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </>
  );
}
